import { Float } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import {
  Component,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import * as THREE from 'three';
import type { StationKind } from '@/content/schema';
import { stationTimeAt } from '@/lib/fx';
import { useSelectionStore } from '@/state/selection';
import { hero, stations, useTourStore } from '@/state/tour';
import { stationImpl } from '@/stations';
import type { Frame } from '@/stations/types';
import { useHeroData } from './data';
import { GlbLayer, type LayerHandle } from './GlbLayer';
import { STORE_LAYER, StoreLayer } from './StoreLayer';

export function Hero() {
  const handles = useRef(new Map<string, LayerHandle>());
  const height = useRef(8);
  const [offset, setOffset] = useState<[number, number, number]>([0, 0, 0]);
  const [ready, setReady] = useState(false);
  const activeIndex = useTourStore((s) => s.activeIndex);
  const kind = stations[activeIndex]!.kind;
  const current = useRef<{ kind: StationKind; frame: Frame } | null>(null);
  const loadData = useHeroData((s) => s.load);

  // The GLB layers place and size the hero; what the visitor builds only ever adds height,
  // so a new storey is not clipped away by a station's default cut.
  const measure = () => {
    const glb = [...handles.current.values()].filter((h) => h.model.id !== STORE_LAYER);
    if (glb.length === 0) return;
    const union = new THREE.Box3();
    for (const h of glb) union.union(h.box);
    let top = union.max.y;
    const store = handles.current.get(STORE_LAYER);
    if (store && !store.box.isEmpty()) top = Math.max(top, store.box.max.y);
    height.current = top - union.min.y;
    return union;
  };

  const onLayerReady = useCallback((id: string, handle: LayerHandle | null) => {
    if (handle) handles.current.set(id, handle);
    else handles.current.delete(id);
    const union = measure();
    if (!union) return;
    const centre = union.getCenter(new THREE.Vector3());
    setOffset([-centre.x, -union.min.y, -centre.z]);
    if (handle) handle.rig.setVisible(handle.model.discipline !== 'space');
    setReady(hero.layers.every((l) => handles.current.has(l.id)));
  }, []);

  const onStoreLayer = useCallback((handle: LayerHandle | null) => {
    if (handle) handles.current.set(STORE_LAYER, handle);
    else handles.current.delete(STORE_LAYER);
    measure();
  }, []);

  useEffect(() => {
    if (ready) void loadData();
  }, [ready, loadData]);

  useFrame((_, dt) => {
    if (handles.current.size === 0) return;
    const { progress } = useTourStore.getState();
    const station = stations[activeIndex]!;
    const frame: Frame = {
      station,
      t: stationTimeAt(station, progress),
      progress,
      dt,
      layers: handles.current,
      height: height.current,
      data: useHeroData.getState().data,
    };
    const impl = stationImpl(kind);
    if (current.current?.kind !== kind) {
      if (current.current) stationImpl(current.current.kind).leave?.(current.current.frame);
      useSelectionStore.getState().setPick(impl.pick);
      useSelectionStore.getState().select(null);
      impl.enter?.(frame);
    }
    current.current = { kind, frame };
    impl.update(frame);
    // The visitor's layer toggles win over whatever the station wants shown.
    const hidden = useSelectionStore.getState().hidden;
    for (const h of handles.current.values()) h.rig.setUserHidden(hidden.includes(h.model.id));
  });

  const StationScene = stationImpl(kind).Scene;

  return (
    <>
      {ready && StationScene && (
        <group position={hero.position}>
          <StationScene />
        </group>
      )}
      <group position={hero.position} rotation-y={hero.rotationY}>
        <ModelErrorBoundary fallback={<Placeholder />}>
          <Suspense fallback={<Placeholder />}>
            <group position={offset}>
              {hero.layers.map((layer) => (
                <GlbLayer key={layer.id} model={layer} onReady={onLayerReady} />
              ))}
              <StoreLayer onLayer={onStoreLayer} />
            </group>
          </Suspense>
        </ModelErrorBoundary>
      </group>
    </>
  );
}

class ModelErrorBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(err: unknown) {
    console.warn('[hero] model failed to load, showing placeholder:', err);
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export function Placeholder() {
  return (
    <Float speed={0.8} rotationIntensity={0} floatIntensity={0.4}>
      <group>
        {[0, 1, 2].map((i) => (
          <group key={i} position={[0, 1.5 + i * 3, 0]}>
            <mesh>
              <boxGeometry args={[24 - i * 4, 2.8, 9 - i * 1.5]} />
              <meshStandardMaterial
                color="#12151b"
                roughness={0.9}
                metalness={0}
                transparent
                opacity={0.85}
              />
            </mesh>
            <lineSegments>
              <edgesGeometry args={[new THREE.BoxGeometry(24 - i * 4, 2.8, 9 - i * 1.5), 15]} />
              <lineBasicMaterial color="#f0b429" />
            </lineSegments>
          </group>
        ))}
      </group>
    </Float>
  );
}
